import Konva from 'konva'
import { normalizeTextSize } from '~/components/Preview/AssetsOverlay/lib'
import { formatRBGColor } from '~/Util'

const createTextParams = (asset, textWidth) => {
  const { settings, text: textString, isInitialized } = asset
  const {
    align, color, font, fontSize, fontStyle,
    outlineColor, outlineWidth, disableOutline, size,
  } = settings

  const textParams = {
    text: textString,
    fontFamily: font,
    fontSize: fontSize * 3,
    fontStyle: `${fontStyle.bold ? 'bold' : ''} ${fontStyle.italic ? 'italic' : ''}`,
    align,
    verticalAlign: 'middle',
    fill: formatRBGColor(color),
    stroke: formatRBGColor(outlineColor),
    strokeWidth: !disableOutline ? outlineWidth : 0,
    fillAfterStrokeEnabled: true,
    wrap: 'word',
    key: 'text',
  }
  const sizeWidth = isInitialized ? size?.width : undefined
  const width = Number.isFinite(textWidth)
    ? textWidth
    : sizeWidth

  if (Number.isFinite(width) && width > 0) {
    textParams.width = width
  }

  return textParams
}

export const getTextMinimumWidth = asset => {
  const textNode = new Konva.Text(createTextParams(asset))
  const words = asset.text.split(/\s+/).filter(Boolean)

  if (!words.length) {
    textNode.destroy()
    return 1
  }

  const longestWordWidth = words.reduce((maxWidth, word) => (
    Math.max(maxWidth, textNode.measureSize(word).width)
  ), 0)
  const strokeWidth = textNode.strokeWidth() || 0

  textNode.destroy()
  return Math.max(longestWordWidth + strokeWidth, 1)
}

export const createText = (asset, { boundingRect,
  onlyAttrs = false, isTransform, textWidth } = {}) => {
  const { settings } = asset
  const {
    background: backgroundColor, fontSize, fontStyle,
    outlineColor, outlineWidth, shadowBlur, shadowColor,
    disableShadow, disableOutline, shadowOffset = { x: 2, y: 2 },
  } = settings

  const text = new Konva.Text(createTextParams(asset, textWidth))

  if (text.getWidth() === 0) {
    text.setWidth(1)
  }

  const shadowStrokeWorkaround = text.clone({
    fill: null,
    stroke: formatRBGColor(outlineColor),
    strokeWidth: !disableOutline ? outlineWidth : 0,
    shadowColor: formatRBGColor({ ...shadowColor, a: 1 }),
    shadowOffset,
    shadowBlur: (fontSize / __CFG__.MAX_FONT_SIZE) * shadowBlur,
    shadowEnabled: !disableShadow,
    wrap: 'word',
    key: 'clone',
  })

  const shadowStrokeWorkaroundRect = shadowStrokeWorkaround.getClientRect()
  const textClientRect = text.getClientRect()
  const skipStrokeWorkaroundRect = shadowStrokeWorkaround.getClientRect({ skipStroke: true })
  const measuredHeight = textClientRect.height - 2 * skipStrokeWorkaroundRect.y
  const measuredWidth = textClientRect.width - 2 * skipStrokeWorkaroundRect.x
  const normalizedSize = normalizeTextSize({
    isMeasured: true,
    textSize: { width: measuredWidth, height: measuredHeight },
    boundingRect,
  })

  const group = new Konva.Group({
    ...normalizedSize,
    y: -shadowStrokeWorkaroundRect.y,
    x: -shadowStrokeWorkaroundRect.x,
    key: 'group',
  })

  const background = new Konva.Rect({
    ...normalizedSize,
    y: shadowStrokeWorkaroundRect.y,
    x: shadowStrokeWorkaroundRect.x,
    fill: formatRBGColor(backgroundColor),
    key: 'background',
  })

  group.add(background)

  if (isTransform) {
    shadowStrokeWorkaround.attrs.y = shadowStrokeWorkaroundRect.y - shadowOffset.y
    shadowStrokeWorkaround.attrs.x = shadowStrokeWorkaroundRect.x
    shadowStrokeWorkaround.attrs.opacity = shadowColor.a
    group.add(shadowStrokeWorkaround)
  } else {
    const image = new Konva.Image({
      ...normalizedSize,
      image: shadowStrokeWorkaround.toCanvas(),
      opacity: shadowColor.a,
      y: shadowStrokeWorkaroundRect.y - shadowOffset.y,
      x: shadowStrokeWorkaroundRect.x,
      key: 'image',
    })
    group.add(image)
  }

  group.add(text)

  if (fontStyle.underline) {
    const underline = new Konva.Rect({
      width: normalizedSize.width,
      height: normalizedSize.height * 0.05,
      fill: formatRBGColor(settings.color),
      position: { y: normalizedSize.height },
      strokeWidth: outlineWidth,
      stroke: formatRBGColor(outlineWidth ? outlineColor : settings.color),
      key: 'rect',
    })
    group.add(underline)
  }

  if (onlyAttrs) {
    const attrs = JSON.parse(JSON.stringify(group.attrs))
    group.destroyChildren()
    group.destroy()
    shadowStrokeWorkaround.destroy()
    return { attrs }
  }

  return group
}
